import {Http} from '../../../Data/config/Http';
import {ProductDTO} from '../models/product.dto';
import {API_ROUTES} from '../../../constants/apiRoutes';
import type {BaseResponse, Pagination} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';
import {hasFiles, buildJsonBodyFromFormData, buildFormDataWithJsonAndFiles} from './productUpdateHelpers';

export const ProductAPIDataSource = {
  getAll: async (page = 1, limit: number = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE): Promise<{ items: ProductDTO[]; pagination?: Pagination }> => {
    try {
      const response = await Http.get<BaseResponse<ProductDTO[]>>(API_ROUTES.PRODUCTS.BASE, {params: {page, limit}});
      const raw = response.data;
      const items = unwrapResponse(raw, 'حدث خطأ في جلب المنتجات');
      return {
        items,
        pagination: raw.pagination
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getById: async (id: number): Promise<ProductDTO> => {
    try {
      const response = await Http.get<BaseResponse<ProductDTO>>(API_ROUTES.PRODUCTS.BY_ID(id.toString()));
      return unwrapResponse(response.data, 'حدث خطأ في جلب بيانات المنتج');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  update: async (id: number, formData: FormData): Promise<ProductDTO> => {
    try {
      // If there are no files in FormData, send JSON to preserve numeric/boolean types expected by backend
      if (!hasFiles(formData)) {
        const jsonBody = buildJsonBodyFromFormData(formData);
        const response = await Http.put<BaseResponse<ProductDTO>>(API_ROUTES.PRODUCTS.BY_ID(id.toString()), jsonBody);
        return unwrapResponse(response.data, 'فشل في تعديل المنتج');
      }

      // Fallback to multipart PUT when there are images/files
      const fd = buildFormDataWithJsonAndFiles(formData);
      const response = await Http.put<BaseResponse<ProductDTO>>(API_ROUTES.PRODUCTS.BY_ID(id.toString()), fd);
      return unwrapResponse(response.data, 'فشل في تعديل المنتج');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  create: async (formData: FormData): Promise<ProductDTO> => {
    try {
      // لا نحدد Content-Type يدوياً؛ Axios سيضبط boundary تلقائياً عند تمرير FormData
      const response = await Http.post<BaseResponse<ProductDTO>>(API_ROUTES.PRODUCTS.BASE, formData);
      return unwrapResponse(response.data, 'حدث خطأ في إنشاء المنتج', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  deleteProduct: async (productId: number): Promise<void> => {
    try {
      const response = await Http.delete<BaseResponse<void>>(API_ROUTES.PRODUCTS.BY_ID(productId.toString()));
      unwrapResponse(response.data, 'فشل في حذف المنتج', [200, 204]);
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  getStats: async (): Promise<Record<string, unknown>> => {
    try {
      const response = await Http.get<BaseResponse<Record<string, unknown>>>(API_ROUTES.PRODUCTS.STATS);
      return unwrapResponse(response.data, 'حدث خطأ في جلب إحصائيات المنتجات');
    } catch (error) {
      handleDataSourceError(error);
    }
  }
};
